import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { withTypes } from 'react-final-form';
import StepsContext from 'providers/Steps/Steps.context';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import { User } from 'models/User';
import useMultiStepForm from 'hooks/useMultiStepForm';
import utils from 'utils';
import { getInitialPhone } from 'phoneNumberUtils';
import { Loader } from 'ncoded-component-library';

import './ProfileFormWrapper.styles.scss';

type ProfileFormContextValue = {
  onCancel: () => void;
};

export const ProfileFormContext = createContext<ProfileFormContextValue>({
  onCancel: utils.noop,
});

type ProfileFormWrapperProps = {
  className?: string;
};

export type ProfileFormValues = Partial<User> & {
  oldPassword: string;
  newPassword: string;
};

const ProfileFormWrapper: React.FC<ProfileFormWrapperProps> = (props) => {
  const { children, className } = props;

  const [isLoading, setIsLoading] = useState(false);

  const { setEditedSteps } = useContext(StepsContext);
  const { profileFormValues, patchCurrentProfile } = useContext(
    CurrentProfileContext,
  );

  const initialData = useMemo(
    () =>
      ({
        phone: getInitialPhone(),
        ...profileFormValues,
      } as ProfileFormValues),
    [profileFormValues],
  );

  const classes = classNames('profile-form-wrapper', className);

  const { Form } = withTypes<ProfileFormValues>();

  const patchUserRequest = useCallback(
    async (values: Partial<ProfileFormValues>) => {
      const {
        oldPassword,
        newPassword,
        profileImage,
        email,
        ...patchableValues
      } = values;

      const linkedinAccount = patchableValues?.linkedinAccount || null;
      const companyName = patchableValues?.companyName || null;
      const companyABN = patchableValues?.companyABN || null;

      try {
        setIsLoading(true);
        await patchCurrentProfile?.(
          {
            ...patchableValues,
            linkedinAccount,
            companyName,
            companyABN,
            ...(email !== initialData?.email && {
              email,
            }),
          },
          {
            onSuccess: () => setIsLoading(false),
          },
        );
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [initialData?.email, patchCurrentProfile],
  );

  const { onPatchStep } = useMultiStepForm<ProfileFormValues, string>({
    name: 'profile',
    redirectOnPatch: false,
    setEditedSteps,
    patchRequest: patchUserRequest,
  });

  return (
    <Form
      onSubmit={onPatchStep}
      initialValues={initialData}
      render={({ handleSubmit, form: { reset } }) => (
        <div className="form-wrapper">
          <ProfileFormContext.Provider value={{ onCancel: reset }}>
            <form onSubmit={handleSubmit} className={classes}>
              {isLoading && <Loader className="fixed without-header" />}
              {children}
            </form>
          </ProfileFormContext.Provider>
        </div>
      )}
    />
  );
};

export default ProfileFormWrapper;
